/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AbortUserSkillAttachmentUploadPathParameters,
  CompleteUserSkillAttachmentUploadPathParameters,
  CreateAttachmentPathParameters,
  DeleteAttachmentPathParameters,
  DownloadUserSkillAttachmentPathParameters,
  GetAttachmentContentPathParameters,
  GetAttachmentPathParameters,
  InitiateUserSkillAttachmentUploadPathParameters,
  ListAttachmentsParams,
  ListAttachmentsPathParameters,
  PutAttachmentContentPathParameters,
  TLHTTPValidationError,
  TLSkillAttachmentDownloadResponse,
  TLSkillAttachmentUploadAbortRequest,
  TLSkillAttachmentUploadAbortResponse,
  TLSkillAttachmentUploadCompleteRequest,
  TLSkillAttachmentUploadInitiateRequest,
  TLSkillAttachmentUploadInitiateResponse,
  TLUserSkillAttachmentContentPutRequest,
  TLUserSkillAttachmentContentResponse,
  TLUserSkillAttachmentCreateRequest,
  TLUserSkillAttachmentListResponse,
  TLUserSkillAttachmentResponse,
  TLUserSkillAttachmentUpdateRequest,
  UpdateAttachmentPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Attachments
 */
export const listAttachments = (
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentListResponse>(
    { url: `/user-skills/${skillId}/attachments`, method: 'GET', params, signal },
    options,
  );
};

export const getListAttachmentsQueryKey = (
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
) => {
  return ['user-skills', skillId, 'attachments', ...(params ? [params] : [])] as const;
};

export const getListAttachmentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAttachmentsQueryKey({ skillId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAttachments>>> = ({ signal }) =>
    listAttachments({ skillId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: skillId !== null && skillId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAttachments>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAttachmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listAttachments>>>;
export type ListAttachmentsQueryError = ErrorType<TLHTTPValidationError>;

export function useListAttachments<
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListAttachmentsPathParameters,
  params: undefined | ListAttachmentsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAttachments>>,
          TError,
          Awaited<ReturnType<typeof listAttachments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAttachments<
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAttachments>>,
          TError,
          Awaited<ReturnType<typeof listAttachments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAttachments<
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Attachments
 */

export function useListAttachments<
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAttachmentsQueryOptions({ skillId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Attachments
 */
export const prefetchListAttachmentsQuery = async <
  TData = Awaited<ReturnType<typeof listAttachments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAttachments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListAttachmentsQueryOptions({ skillId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Attachments
 */
export const invalidateListAttachments = async (
  queryClient: QueryClient,
  { skillId }: ListAttachmentsPathParameters,
  params?: ListAttachmentsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListAttachmentsQueryKey({ skillId }, params) }, options);

  return queryClient;
};

/**
 * @summary Create Attachment
 */
export const createAttachment = (
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentResponse>(
    {
      url: `/user-skills/${skillId}/attachments`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLUserSkillAttachmentCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateAttachmentQueryKey = (
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest?: BodyType<TLUserSkillAttachmentCreateRequest>,
) => {
  return ['POST', 'user-skills', skillId, 'attachments', tLUserSkillAttachmentCreateRequest] as const;
};

export const getCreateAttachmentQueryOptions = <
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateAttachmentQueryKey({ skillId }, tLUserSkillAttachmentCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createAttachment>>> = ({ signal }) =>
    createAttachment({ skillId }, tLUserSkillAttachmentCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: skillId !== null && skillId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createAttachment>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof createAttachment>>>;
export type CreateAttachmentQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateAttachment<
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createAttachment>>,
          TError,
          Awaited<ReturnType<typeof createAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateAttachment<
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createAttachment>>,
          TError,
          Awaited<ReturnType<typeof createAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateAttachment<
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Attachment
 */

export function useCreateAttachment<
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateAttachmentQueryOptions({ skillId }, tLUserSkillAttachmentCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Attachment
 */
export const prefetchCreateAttachmentQuery = async <
  TData = Awaited<ReturnType<typeof createAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateAttachmentQueryOptions({ skillId }, tLUserSkillAttachmentCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Attachment
 */
export const invalidateCreateAttachment = async (
  queryClient: QueryClient,
  { skillId }: CreateAttachmentPathParameters,
  tLUserSkillAttachmentCreateRequest: BodyType<TLUserSkillAttachmentCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateAttachmentQueryKey({ skillId }, tLUserSkillAttachmentCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Abort User Skill Attachment Upload
 */
export const abortUserSkillAttachmentUpload = (
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSkillAttachmentUploadAbortResponse>(
    {
      url: `/user-skills/${skillId}/attachments/upload/abort`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSkillAttachmentUploadAbortRequest,
      signal,
    },
    options,
  );
};

export const getAbortUserSkillAttachmentUploadQueryKey = (
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest?: BodyType<TLSkillAttachmentUploadAbortRequest>,
) => {
  return [
    'POST',
    'user-skills',
    skillId,
    'attachments',
    'upload',
    'abort',
    tLSkillAttachmentUploadAbortRequest,
  ] as const;
};

export const getAbortUserSkillAttachmentUploadQueryOptions = <
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getAbortUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadAbortRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>> = ({ signal }) =>
    abortUserSkillAttachmentUpload({ skillId }, tLSkillAttachmentUploadAbortRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: skillId !== null && skillId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AbortUserSkillAttachmentUploadQueryResult = NonNullable<
  Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>
>;
export type AbortUserSkillAttachmentUploadQueryError = ErrorType<TLHTTPValidationError>;

export function useAbortUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAbortUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAbortUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Abort User Skill Attachment Upload
 */

export function useAbortUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAbortUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadAbortRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Abort User Skill Attachment Upload
 */
export const prefetchAbortUserSkillAttachmentUploadQuery = async <
  TData = Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof abortUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAbortUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadAbortRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Abort User Skill Attachment Upload
 */
export const invalidateAbortUserSkillAttachmentUpload = async (
  queryClient: QueryClient,
  { skillId }: AbortUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadAbortRequest: BodyType<TLSkillAttachmentUploadAbortRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAbortUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadAbortRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Complete User Skill Attachment Upload
 */
export const completeUserSkillAttachmentUpload = (
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentResponse>(
    {
      url: `/user-skills/${skillId}/attachments/upload/complete`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSkillAttachmentUploadCompleteRequest,
      signal,
    },
    options,
  );
};

export const getCompleteUserSkillAttachmentUploadQueryKey = (
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest?: BodyType<TLSkillAttachmentUploadCompleteRequest>,
) => {
  return [
    'POST',
    'user-skills',
    skillId,
    'attachments',
    'upload',
    'complete',
    tLSkillAttachmentUploadCompleteRequest,
  ] as const;
};

export const getCompleteUserSkillAttachmentUploadQueryOptions = <
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getCompleteUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadCompleteRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>> = ({ signal }) =>
    completeUserSkillAttachmentUpload({ skillId }, tLSkillAttachmentUploadCompleteRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: skillId !== null && skillId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CompleteUserSkillAttachmentUploadQueryResult = NonNullable<
  Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>
>;
export type CompleteUserSkillAttachmentUploadQueryError = ErrorType<TLHTTPValidationError>;

export function useCompleteUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompleteUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCompleteUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Complete User Skill Attachment Upload
 */

export function useCompleteUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCompleteUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadCompleteRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Complete User Skill Attachment Upload
 */
export const prefetchCompleteUserSkillAttachmentUploadQuery = async <
  TData = Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof completeUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCompleteUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadCompleteRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Complete User Skill Attachment Upload
 */
export const invalidateCompleteUserSkillAttachmentUpload = async (
  queryClient: QueryClient,
  { skillId }: CompleteUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadCompleteRequest: BodyType<TLSkillAttachmentUploadCompleteRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCompleteUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadCompleteRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Initiate User Skill Attachment Upload
 */
export const initiateUserSkillAttachmentUpload = (
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSkillAttachmentUploadInitiateResponse>(
    {
      url: `/user-skills/${skillId}/attachments/upload/init`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSkillAttachmentUploadInitiateRequest,
      signal,
    },
    options,
  );
};

export const getInitiateUserSkillAttachmentUploadQueryKey = (
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest?: BodyType<TLSkillAttachmentUploadInitiateRequest>,
) => {
  return [
    'POST',
    'user-skills',
    skillId,
    'attachments',
    'upload',
    'init',
    tLSkillAttachmentUploadInitiateRequest,
  ] as const;
};

export const getInitiateUserSkillAttachmentUploadQueryOptions = <
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getInitiateUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadInitiateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>> = ({ signal }) =>
    initiateUserSkillAttachmentUpload({ skillId }, tLSkillAttachmentUploadInitiateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: skillId !== null && skillId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type InitiateUserSkillAttachmentUploadQueryResult = NonNullable<
  Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>
>;
export type InitiateUserSkillAttachmentUploadQueryError = ErrorType<TLHTTPValidationError>;

export function useInitiateUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInitiateUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
          TError,
          Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useInitiateUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Initiate User Skill Attachment Upload
 */

export function useInitiateUserSkillAttachmentUpload<
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getInitiateUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadInitiateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Initiate User Skill Attachment Upload
 */
export const prefetchInitiateUserSkillAttachmentUploadQuery = async <
  TData = Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof initiateUserSkillAttachmentUpload>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getInitiateUserSkillAttachmentUploadQueryOptions(
    { skillId },
    tLSkillAttachmentUploadInitiateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Initiate User Skill Attachment Upload
 */
export const invalidateInitiateUserSkillAttachmentUpload = async (
  queryClient: QueryClient,
  { skillId }: InitiateUserSkillAttachmentUploadPathParameters,
  tLSkillAttachmentUploadInitiateRequest: BodyType<TLSkillAttachmentUploadInitiateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getInitiateUserSkillAttachmentUploadQueryKey({ skillId }, tLSkillAttachmentUploadInitiateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Attachment
 */
export const deleteAttachment = (
  { skillId, attachmentId }: DeleteAttachmentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/user-skills/${skillId}/attachments/${attachmentId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteAttachmentQueryKey = ({ skillId, attachmentId }: DeleteAttachmentPathParameters) => {
  return ['DELETE', 'user-skills', skillId, 'attachments', attachmentId] as const;
};

export const getDeleteAttachmentQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: DeleteAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteAttachmentQueryKey({ skillId, attachmentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteAttachment>>> = ({ signal }) =>
    deleteAttachment({ skillId, attachmentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof deleteAttachment>>>;
export type DeleteAttachmentQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteAttachment<
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteAttachmentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteAttachment>>,
          TError,
          Awaited<ReturnType<typeof deleteAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteAttachment<
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteAttachment>>,
          TError,
          Awaited<ReturnType<typeof deleteAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteAttachment<
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Attachment
 */

export function useDeleteAttachment<
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: DeleteAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteAttachmentQueryOptions({ skillId, attachmentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Attachment
 */
export const prefetchDeleteAttachmentQuery = async <
  TData = Awaited<ReturnType<typeof deleteAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: DeleteAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteAttachmentQueryOptions({ skillId, attachmentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Attachment
 */
export const invalidateDeleteAttachment = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: DeleteAttachmentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteAttachmentQueryKey({ skillId, attachmentId }) }, options);

  return queryClient;
};

/**
 * @summary Get Attachment
 */
export const getAttachment = (
  { skillId, attachmentId }: GetAttachmentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentResponse>(
    { url: `/user-skills/${skillId}/attachments/${attachmentId}`, method: 'GET', signal },
    options,
  );
};

export const getGetAttachmentQueryKey = ({ skillId, attachmentId }: GetAttachmentPathParameters) => {
  return ['user-skills', skillId, 'attachments', attachmentId] as const;
};

export const getGetAttachmentQueryOptions = <
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: GetAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAttachmentQueryKey({ skillId, attachmentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAttachment>>> = ({ signal }) =>
    getAttachment({ skillId, attachmentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof getAttachment>>>;
export type GetAttachmentQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAttachment<
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAttachment>>,
          TError,
          Awaited<ReturnType<typeof getAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAttachment<
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAttachment>>,
          TError,
          Awaited<ReturnType<typeof getAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAttachment<
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Attachment
 */

export function useGetAttachment<
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: GetAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAttachmentQueryOptions({ skillId, attachmentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Attachment
 */
export const prefetchGetAttachmentQuery = async <
  TData = Awaited<ReturnType<typeof getAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: GetAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAttachmentQueryOptions({ skillId, attachmentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Attachment
 */
export const invalidateGetAttachment = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: GetAttachmentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAttachmentQueryKey({ skillId, attachmentId }) }, options);

  return queryClient;
};

/**
 * @summary Update Attachment
 */
export const updateAttachment = (
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentResponse>(
    {
      url: `/user-skills/${skillId}/attachments/${attachmentId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUserSkillAttachmentUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateAttachmentQueryKey = (
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest?: BodyType<TLUserSkillAttachmentUpdateRequest>,
) => {
  return ['PATCH', 'user-skills', skillId, 'attachments', attachmentId, tLUserSkillAttachmentUpdateRequest] as const;
};

export const getUpdateAttachmentQueryOptions = <
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateAttachmentQueryKey({ skillId, attachmentId }, tLUserSkillAttachmentUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateAttachment>>> = ({ signal }) =>
    updateAttachment({ skillId, attachmentId }, tLUserSkillAttachmentUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof updateAttachment>>>;
export type UpdateAttachmentQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateAttachment<
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateAttachment>>,
          TError,
          Awaited<ReturnType<typeof updateAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateAttachment<
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateAttachment>>,
          TError,
          Awaited<ReturnType<typeof updateAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateAttachment<
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Attachment
 */

export function useUpdateAttachment<
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateAttachmentQueryOptions(
    { skillId, attachmentId },
    tLUserSkillAttachmentUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Attachment
 */
export const prefetchUpdateAttachmentQuery = async <
  TData = Awaited<ReturnType<typeof updateAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateAttachmentQueryOptions(
    { skillId, attachmentId },
    tLUserSkillAttachmentUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Attachment
 */
export const invalidateUpdateAttachment = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: UpdateAttachmentPathParameters,
  tLUserSkillAttachmentUpdateRequest: BodyType<TLUserSkillAttachmentUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateAttachmentQueryKey({ skillId, attachmentId }, tLUserSkillAttachmentUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Attachment Content
 */
export const getAttachmentContent = (
  { skillId, attachmentId }: GetAttachmentContentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentContentResponse>(
    { url: `/user-skills/${skillId}/attachments/${attachmentId}/content`, method: 'GET', signal },
    options,
  );
};

export const getGetAttachmentContentQueryKey = ({ skillId, attachmentId }: GetAttachmentContentPathParameters) => {
  return ['user-skills', skillId, 'attachments', attachmentId, 'content'] as const;
};

export const getGetAttachmentContentQueryOptions = <
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: GetAttachmentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAttachmentContentQueryKey({ skillId, attachmentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAttachmentContent>>> = ({ signal }) =>
    getAttachmentContent({ skillId, attachmentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAttachmentContentQueryResult = NonNullable<Awaited<ReturnType<typeof getAttachmentContent>>>;
export type GetAttachmentContentQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAttachmentContent<
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentContentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAttachmentContent>>,
          TError,
          Awaited<ReturnType<typeof getAttachmentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAttachmentContent<
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAttachmentContent>>,
          TError,
          Awaited<ReturnType<typeof getAttachmentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAttachmentContent<
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetAttachmentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Attachment Content
 */

export function useGetAttachmentContent<
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: GetAttachmentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAttachmentContentQueryOptions({ skillId, attachmentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Attachment Content
 */
export const prefetchGetAttachmentContentQuery = async <
  TData = Awaited<ReturnType<typeof getAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: GetAttachmentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAttachmentContentQueryOptions({ skillId, attachmentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Attachment Content
 */
export const invalidateGetAttachmentContent = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: GetAttachmentContentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetAttachmentContentQueryKey({ skillId, attachmentId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Put Attachment Content
 */
export const putAttachmentContent = (
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserSkillAttachmentContentResponse>(
    {
      url: `/user-skills/${skillId}/attachments/${attachmentId}/content`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLUserSkillAttachmentContentPutRequest,
      signal,
    },
    options,
  );
};

export const getPutAttachmentContentQueryKey = (
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest?: BodyType<TLUserSkillAttachmentContentPutRequest>,
) => {
  return [
    'PUT',
    'user-skills',
    skillId,
    'attachments',
    attachmentId,
    'content',
    tLUserSkillAttachmentContentPutRequest,
  ] as const;
};

export const getPutAttachmentContentQueryOptions = <
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPutAttachmentContentQueryKey({ skillId, attachmentId }, tLUserSkillAttachmentContentPutRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof putAttachmentContent>>> = ({ signal }) =>
    putAttachmentContent({ skillId, attachmentId }, tLUserSkillAttachmentContentPutRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PutAttachmentContentQueryResult = NonNullable<Awaited<ReturnType<typeof putAttachmentContent>>>;
export type PutAttachmentContentQueryError = ErrorType<TLHTTPValidationError>;

export function usePutAttachmentContent<
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof putAttachmentContent>>,
          TError,
          Awaited<ReturnType<typeof putAttachmentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutAttachmentContent<
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof putAttachmentContent>>,
          TError,
          Awaited<ReturnType<typeof putAttachmentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutAttachmentContent<
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Put Attachment Content
 */

export function usePutAttachmentContent<
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPutAttachmentContentQueryOptions(
    { skillId, attachmentId },
    tLUserSkillAttachmentContentPutRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Put Attachment Content
 */
export const prefetchPutAttachmentContentQuery = async <
  TData = Awaited<ReturnType<typeof putAttachmentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putAttachmentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPutAttachmentContentQueryOptions(
    { skillId, attachmentId },
    tLUserSkillAttachmentContentPutRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Put Attachment Content
 */
export const invalidatePutAttachmentContent = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: PutAttachmentContentPathParameters,
  tLUserSkillAttachmentContentPutRequest: BodyType<TLUserSkillAttachmentContentPutRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPutAttachmentContentQueryKey({ skillId, attachmentId }, tLUserSkillAttachmentContentPutRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Download User Skill Attachment
 */
export const downloadUserSkillAttachment = (
  { skillId, attachmentId }: DownloadUserSkillAttachmentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSkillAttachmentDownloadResponse>(
    { url: `/user-skills/${skillId}/attachments/${attachmentId}/download`, method: 'GET', signal },
    options,
  );
};

export const getDownloadUserSkillAttachmentQueryKey = ({
  skillId,
  attachmentId,
}: DownloadUserSkillAttachmentPathParameters) => {
  return ['user-skills', skillId, 'attachments', attachmentId, 'download'] as const;
};

export const getDownloadUserSkillAttachmentQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: DownloadUserSkillAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDownloadUserSkillAttachmentQueryKey({ skillId, attachmentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadUserSkillAttachment>>> = ({ signal }) =>
    downloadUserSkillAttachment({ skillId, attachmentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: skillId !== null && skillId !== undefined && attachmentId !== null && attachmentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DownloadUserSkillAttachmentQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadUserSkillAttachment>>
>;
export type DownloadUserSkillAttachmentQueryError = ErrorType<TLHTTPValidationError>;

export function useDownloadUserSkillAttachment<
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadUserSkillAttachmentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
          TError,
          Awaited<ReturnType<typeof downloadUserSkillAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadUserSkillAttachment<
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadUserSkillAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
          TError,
          Awaited<ReturnType<typeof downloadUserSkillAttachment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadUserSkillAttachment<
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadUserSkillAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Download User Skill Attachment
 */

export function useDownloadUserSkillAttachment<
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { skillId, attachmentId }: DownloadUserSkillAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDownloadUserSkillAttachmentQueryOptions({ skillId, attachmentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Download User Skill Attachment
 */
export const prefetchDownloadUserSkillAttachmentQuery = async <
  TData = Awaited<ReturnType<typeof downloadUserSkillAttachment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { skillId, attachmentId }: DownloadUserSkillAttachmentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadUserSkillAttachment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDownloadUserSkillAttachmentQueryOptions({ skillId, attachmentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Download User Skill Attachment
 */
export const invalidateDownloadUserSkillAttachment = async (
  queryClient: QueryClient,
  { skillId, attachmentId }: DownloadUserSkillAttachmentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDownloadUserSkillAttachmentQueryKey({ skillId, attachmentId }) },
    options,
  );

  return queryClient;
};
